import { AnvilIcon } from "@/components/icons/AnvilIcon";
import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Play, Loader2, Send, Square, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { TakeoffToolsBadge } from "@/components/deal/TakeoffToolsBadge";
import { RepsCertsChip } from "@/components/deal/RepsCertsChip";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// Live-feed status toast — never leave the operator on a blank page with no signal while the SSE run feed is
// (re)connecting. Fixed id so it stays a single toast and clears the instant the stream is live.
const FEED_TOAST_ID = "live-feed-reconnect";
const showFeedToast = () => toast.loading("Reconnecting to live progress…", { id: FEED_TOAST_ID });
const clearFeedToast = () => toast.dismiss(FEED_TOAST_ID);

/* Unified scrollable feed (live run steps + chat) — the in-bid Anvil execution cockpit. */
export function AnvilCockpit({ deal, reload, startSignal, onAutoRun, canRun }) {
  const [steps, setSteps] = useState([]);
  const [running, setRunning] = useState(deal.engine_status === "running");
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [queued, setQueued] = useState(deal.engine_status === "running" && !!deal.engine_queued);
  const esRef = useRef(null);
  const feedRef = useRef(null);
  const resyncTimer = useRef(null);

  useEffect(() => {
    api.get(`/bids/${deal.id}/chat`).then((r) => setMsgs(r.data.history || [])).catch(() => {});
    return () => {
      if (esRef.current) esRef.current.close();
      if (resyncTimer.current) { clearTimeout(resyncTimer.current); resyncTimer.current = null; }
      clearFeedToast();
    };
    /* eslint-disable-next-line */
  }, [deal.id]);
  useEffect(() => { if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight; }, [steps, msgs, busy]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fire startRun only on an explicit start signal
  useEffect(() => { if (startSignal > 0) startRun(); }, [startSignal]);
  // Dir 70 — RE-ATTACH: if a run is already going (e.g., you navigated away and came back), tail it live.
  useEffect(() => {
    if (deal.engine_status === "running" && !esRef.current) attachStream();
    /* eslint-disable-next-line */
  }, [deal.engine_status]);

  const pushStep = (s) => setSteps((arr) => [...arr, s]);
  const updateRunning = (fn) => setSteps((arr) => {
    const copy = [...arr];
    for (let i = copy.length - 1; i >= 0; i--) { if (copy[i].status === "running") { copy[i] = fn({ ...copy[i] }); break; } }
    return copy;
  });
  const markStageDone = (d) => setSteps((arr) => {
    const copy = [...arr];
    for (let i = copy.length - 1; i >= 0; i--) {
      if (copy[i].status === "running") { copy[i] = { ...copy[i], status: "done", live: "", summary: d.summary || d.evidence }; return copy; }
    }
    copy.push({ n: d.number, name: d.name, status: "done", summary: d.summary || d.evidence });
    return copy;
  });

  // Dir 70 — PURE TAIL: opening this stream never starts work; it only relays the background run. Closing it
  // (navigation) leaves the run going; re-opening re-attaches and rebuilds the timeline from the snapshot.
  const attachStream = () => {
    if (esRef.current) esRef.current.close();
    setRunning(true);
    showFeedToast();  // "Reconnecting to live progress…" — shows immediately on (re)attach, clears once live
    const es = new EventSource(`${API_BASE}/bids/${deal.id}/engine/stream`, { withCredentials: true });
    esRef.current = es;
    const clearResync = () => { if (resyncTimer.current) { clearTimeout(resyncTimer.current); resyncTimer.current = null; } };
    const finish = () => { es.close(); esRef.current = null; clearResync(); setRunning(false); setReconnecting(false); setQueued(false); clearFeedToast(); reload(); };
    es.onopen = () => { setReconnecting(false); clearResync(); clearFeedToast(); };  // stream (re)connected — we're live again
    es.onmessage = (ev) => {
      setReconnecting(false); clearResync(); clearFeedToast();
      let d; try { d = JSON.parse(ev.data); } catch { return; }
      if (d.type === "start") setSteps([
        { n: "1", name: "Pursue (showstopper gate)", status: "done", summary: "Decision: pursue" },
        { n: "•", name: d.message, status: "info" }]);
      else if (d.type === "stage_start") { setQueued(false); pushStep({ n: d.number, name: d.name, status: "running", live: "" }); }
      else if (d.type === "token") updateRunning((s) => ({ ...s, live: ((s.live || "") + d.text).slice(-220) }));
      else if (d.type === "stage_skip") pushStep({ n: d.number, name: d.name, status: "done", summary: "already complete" });
      else if (d.type === "queued") { setQueued(true); pushStep({ n: "⏸", name: d.message, status: "info" }); }
      else if (d.type === "stage_done") { markStageDone(d); reload(); }
      else if (d.type === "heartbeat") { /* keepalive — run still going */ }
      else if (d.type === "done") { pushStep({ n: "✅", name: d.message, status: "info" }); finish(); if (!d.idle) toast.success("Anvil finished all stages."); }
      else if (d.type === "stopped") { pushStep({ n: "■", name: d.message, status: "info" }); finish(); }
      else if (d.type === "error") { pushStep({ n: "⚠", name: d.message, status: "error" }); finish(); }
      else if (d.type === "advisory" && d.kind) pushStep({ n: "⚠", name: d.message, status: "warn" });  // Dir 209 — post-Proceed advisory; run keeps going
      else if (d.type === "partial") { pushStep({ n: "◑", name: d.message, status: "info" }); finish(); }  // non-biddable → capability statement / forms only
    };
    // P0 (2026-07-13): SSE dropped. Show 'reconnecting…' and, after a short grace, ask the server for the
    // TRUE engine state so a crashed/redeployed run stops showing as 'running' forever. EventSource keeps
    // auto-reconnecting underneath; if the backend is truly down the global unreachable banner covers it.
    es.onerror = () => {
      setReconnecting(true);
      showFeedToast();
      if (!resyncTimer.current) resyncTimer.current = setTimeout(() => { resyncTimer.current = null; resyncState(finish); }, 8000);
    };
  };
  const resyncState = async (finish) => {
    try {
      const r = await api.get(`/bids/${deal.id}/engine/status`);
      const s = r.data || {};
      if (s.engine_status && s.engine_status !== "running") {
        pushStep({ n: s.engine_status === "error" ? "⚠" : "✅",
                   name: s.engine_error || `Run ${s.engine_status}`,
                   status: s.engine_status === "error" ? "error" : "info" });
        if (typeof finish === "function") finish();
        else { if (esRef.current) { esRef.current.close(); esRef.current = null; } setRunning(false); setReconnecting(false); clearFeedToast(); reload(); }
      }
      // still 'running' server-side => genuine transient drop; keep tailing (EventSource auto-reconnects).
    } catch { /* backend unreachable — keep 'reconnecting'; the global banner explains the outage. */ }
  };
  const startRun = async () => {
    try {
      await api.post(`/bids/${deal.id}/auto-run`);
    } catch (e) {
      if (e?.response?.status !== 400) { toast.error(e?.response?.data?.detail || "Couldn't start the run."); return; }
      // 400 = already running → just attach and tail
    }
    attachStream();
  };
  const stopRun = async () => {
    try { await api.post(`/bids/${deal.id}/engine/stop`); } catch { /* ignore */ }
    if (esRef.current) { esRef.current.close(); esRef.current = null; }
    if (resyncTimer.current) { clearTimeout(resyncTimer.current); resyncTimer.current = null; }
    setRunning(false); setReconnecting(false); clearFeedToast(); pushStep({ n: "■", name: "Stopping…", status: "info" }); reload();
  };

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const history = msgs.map((m) => ({ role: m.role, content: m.content }));
    setMsgs((m) => [...m, { role: "user", content: text }]);
    setInput(""); setBusy(true);
    try {
      const r = await api.post(`/bids/${deal.id}/chat`, { message: text, history });
      setMsgs((m) => [...m, { role: "assistant", content: r.data.reply, actions: r.data.actions || [] }]);
      if ((r.data.actions || []).some((a) => a.ok)) window.dispatchEvent(new CustomEvent("ironclad-refresh"));
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", content: e?.response?.data?.detail || "Anvil hit an error." }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-zinc-200 shadow-sm rounded-xl overflow-hidden flex flex-col h-[80vh]" data-testid="anvil-cockpit">
      <div className="flex items-center justify-between px-4 py-3 bg-zinc-950 text-white shrink-0">
        <div className="flex items-center gap-2.5">
          <span className="h-8 w-8 rounded-full bg-gold/20 flex items-center justify-center"><AnvilIcon className="h-5 w-5 text-gold" /></span>
          <div className="leading-tight">
            <div className="font-display font-bold text-base tracking-wide">Anvil · this bid</div>
            <div className="text-[9px] tracking-[0.12em] text-zinc-400">CLAUDE OPUS 4.8 · WEB SEARCH · READS YOUR DOCS</div>
          </div>
        </div>
        {running ? (
          <div className="flex items-center gap-2">
            <RepsCertsChip dealId={deal.id} />
            <TakeoffToolsBadge />
            {reconnecting && (
              <span data-testid="cockpit-reconnecting" className="flex items-center gap-1.5 text-[11px] text-amber-300">
                <Loader2 className="h-3 w-3 animate-spin" /> reconnecting…
              </span>
            )}
            {queued && !reconnecting && (
              <span data-testid="cockpit-queued" className="flex items-center gap-1.5 text-[11px] text-amber-300">
                <Loader2 className="h-3 w-3 animate-spin" /> Queued — will start shortly
              </span>
            )}
            <button data-testid="cockpit-stop-btn" onClick={stopRun} className="flex items-center gap-1.5 text-xs bg-red-500/20 text-red-300 px-3 py-1.5 rounded-md hover:bg-red-500/30"><Square className="h-3.5 w-3.5" /> Stop</button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <RepsCertsChip dealId={deal.id} />
            <TakeoffToolsBadge />
            {canRun ? (
              <button data-testid="cockpit-run-btn" onClick={onAutoRun} className="flex items-center gap-1.5 text-xs bg-gold/20 text-gold px-3 py-1.5 rounded-md hover:bg-gold/30"><Play className="h-3.5 w-3.5" /> Auto-run</button>
            ) : null}
          </div>
        )}
      </div>

      <div ref={feedRef} className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="cockpit-feed">
        {(deal.engine_advisories || []).length > 0 && (
          <div data-testid="cockpit-advisories" className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 space-y-1">
            <div className="text-[11px] font-semibold text-amber-800 flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" /> Advisories — you proceeded at Stage 1; the engine flagged these but kept running
            </div>
            {(deal.engine_advisories || []).map((a, i) => (
              <div key={i} className="text-[11px] text-amber-800 leading-snug" data-testid={`advisory-${i}`}>
                • {typeof a === "string" ? a : (a.reason || "")}
              </div>
            ))}
          </div>
        )}

        {steps.length === 0 && msgs.length === 0 && (
          <div className="text-xs text-zinc-500 leading-relaxed bg-zinc-50 rounded-lg p-3">
            Hit <span className="font-semibold text-zinc-700">Auto-run</span> to watch Anvil (Opus 4.8) work the 7+7B
            stages live — brief steps appear here and the preview fills on the right. You can <span className="font-semibold">Stop</span> any
            time and critique below. Or just chat: price a task, draft a section, read a doc, research wages.
          </div>
        )}

        {steps.length > 0 && (
          <div className="space-y-1.5" data-testid="cockpit-steps">
            {steps.map((s, i) => (
              <div key={i} className="font-mono text-[12px] leading-relaxed">
                {s.status === "info" ? (
                  <div className="text-zinc-500">{s.n} {s.name}</div>
                ) : s.status === "error" ? (
                  <div className="text-red-500">{s.n} {s.name}</div>
                ) : s.status === "warn" ? (
                  <div data-testid="cockpit-advisory-step" className="rounded-md px-2.5 py-1.5 bg-amber-50 border border-amber-200 text-amber-800 font-sans text-xs flex items-start gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /><span><span className="font-semibold">Advisory</span> — {s.name}</span>
                  </div>
                ) : (
                  <div className={`rounded-md px-2.5 py-1.5 ${s.status === "running" ? "bg-amber-50 border border-amber-200" : "bg-emerald-50 border border-emerald-200"}`}>
                    <div className="flex items-center gap-2">
                      {s.status === "running" ? <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-600" /> : <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
                      <span className="text-zinc-800 font-sans text-xs font-medium">Stage {s.n} — {s.name}</span>
                    </div>
                    {s.status === "running" && s.live && <div className="text-zinc-400 mt-1 truncate">{s.live}</div>}
                    {s.summary && <div className="text-emerald-700 font-sans text-[11px] mt-0.5 pl-5.5">✓ {s.summary}</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {msgs.map((m, i) => (
          <div key={`m${i}`} className={`text-sm ${m.role === "user" ? "text-right" : ""}`}>
            <div className={`inline-block px-3 py-2 rounded-lg max-w-[88%] whitespace-pre-wrap ${m.role === "user" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-900"}`}>{m.content}</div>
            {(m.actions || []).filter((a) => a.ok).length > 0 && (
              <div className="mt-1 text-[10px] text-emerald-600">✓ {m.actions.filter((a) => a.ok).map((a) => a.tool).join(", ")}</div>
            )}
          </div>
        ))}
        {busy && <div className="flex items-center gap-2 text-xs text-zinc-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Anvil is working…</div>}
      </div>

      <div className="p-3 border-t border-zinc-200 flex items-center gap-2 shrink-0">
        <input data-testid="project-chat-input" value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()} placeholder={running ? "Critique or steer Anvil…" : "Ask Anvil about this bid…"}
          className="flex-1 bg-white border border-zinc-300 rounded-md px-3 py-2 text-sm text-zinc-900 outline-none focus:border-zinc-500" />
        <button data-testid="project-chat-send" onClick={send} disabled={busy} className="h-9 w-9 rounded-md bg-zinc-900 text-white flex items-center justify-center disabled:opacity-50"><Send className="h-4 w-4" /></button>
      </div>
    </div>
  );
}
